import { useState, useEffect } from 'react';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    setIsMenuOpen(false);
    if (window.location.pathname !== '/') {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      const section = document.getElementById(sectionId);
      section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/98 backdrop-blur-md shadow-lg' 
          : 'bg-white/95 backdrop-blur-sm shadow-sm'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer group"
            onClick={() => scrollToSection('inicio')}
          >
            <img 
              src="/images/logo-original.jpg" 
              alt="Rebel Rabbit Logo" 
              className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {[
              { label: 'Inicio', id: 'inicio' },
              { label: 'Servicios', id: 'servicios' },
              { label: 'Tatuadoras', id: 'tatuadoras' },
              { label: 'Galería', id: 'galeria', isRoute: true },
              { label: 'Contacto', id: 'contacto' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.isRoute) {
                    window.REACT_APP_NAVIGATE('/galeria');
                  } else {
                    scrollToSection(item.id);
                  }
                }}
                className="relative px-4 py-2 text-gray-700 font-medium transition-colors hover:text-pink-500 group"
              >
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-pink-500 transition-all duration-300 group-hover:w-full"></span>
              </button>
            ))}
          </nav>

          {/* Social Links & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="https://instagram.com/rebel_rabbit_tattoo"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-pink-500 transition-colors"
              aria-label="Instagram"
            >
              <i className="ri-instagram-line text-xl"></i>
            </a>
            <a 
              href="https://wa.me/56982504829?text=Hola, quiero reservar una cita en Rebel Rabbit"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-pink-500 to-pink-600 text-white px-6 py-2.5 rounded-full font-semibold hover:from-pink-600 hover:to-pink-700 transition-all shadow-md hover:shadow-lg transform hover:scale-105 whitespace-nowrap flex items-center"
            >
              <i className="ri-whatsapp-line mr-2 text-lg"></i>
              Reservar Cita
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-pink-500 transition-colors"
            aria-label="Toggle menu"
          >
            <i className={`text-2xl transition-transform duration-300 ${
              isMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'
            }`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="py-4 border-t border-gray-100">
            <div className="flex flex-col space-y-1">
              {[
                { label: 'Inicio', id: 'inicio', icon: 'ri-home-line' },
                { label: 'Servicios', id: 'servicios', icon: 'ri-service-line' },
                { label: 'Tatuadoras', id: 'tatuadoras', icon: 'ri-team-line' },
                { label: 'Galería', id: 'galeria', icon: 'ri-gallery-line', isRoute: true },
                { label: 'Contacto', id: 'contacto', icon: 'ri-mail-line' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.isRoute) {
                      setIsMenuOpen(false);
                      window.REACT_APP_NAVIGATE('/galeria');
                    } else {
                      scrollToSection(item.id);
                    }
                  }}
                  className="flex items-center px-4 py-3 text-gray-700 hover:text-pink-500 hover:bg-pink-50 rounded-lg transition-all font-medium"
                >
                  <i className={`${item.icon} mr-3 text-lg`}></i>
                  {item.label}
                </button>
              ))}
              
              <div className="pt-4 px-4 space-y-3">
                <a
                  href="https://instagram.com/rebel_rabbit_tattoo"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center justify-center w-full px-4 py-3 text-pink-500 border-2 border-pink-500 rounded-lg font-semibold hover:bg-pink-50 transition-all"
                >
                  <i className="ri-instagram-line mr-2 text-lg"></i>
                  Síguenos en Instagram
                </a>
                <a 
                  href="https://wa.me/56982504829?text=Hola, quiero reservar una cita en Rebel Rabbit"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center justify-center w-full bg-gradient-to-r from-pink-500 to-pink-600 text-white px-4 py-3 rounded-lg font-semibold hover:from-pink-600 hover:to-pink-700 transition-all shadow-md"
                >
                  <i className="ri-whatsapp-line mr-2 text-lg"></i>
                  Reservar por WhatsApp
                </a>
              </div>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}
